import { Suspense, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import {
    ActuatorAccessoriesPageData,
    ActuatorAccessoriesPageVariables,
    getActuatorAccessoriesPageConfig,
} from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { Query } from 'src/modules/common/components/Query/Query.tsx';
import { buildCheckoutVariables } from 'src/actuators/checkout/buildCheckoutVariables.ts';
import { LocationState } from 'src/actuators/LocationState.ts';
import { getFilterValuesFromUrl } from 'src/utils/getFilterValuesFromUrl.ts';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';

type FetchActuatorSelectionProps = {
    locationState: LocationState;
    searchParams: string;
}

export const FetchActuatorSelection = ({
    locationState,
    searchParams,
} : FetchActuatorSelectionProps) => {
    const {
        actuatorLabel,
    } = locationState;

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const pageConfig = useMemo(() => getActuatorAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);

    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const filter = getFilterValuesFromUrl(new URLSearchParams(searchParams), [
        ...pageConfig.mainFilterConfig,
        ...pageConfig.additionalFilterConfig,
    ]);

    const variables = useMemo(() => buildCheckoutVariables({
        mandatoryFilterConfig,
        filter,
        actuatorLabel,
        userSettings,
    }), [
        mandatoryFilterConfig,
        filter,
        actuatorLabel,
        userSettings,
    ]);

    return (
        <Suspense fallback={<SkeletonSelection/>}>
            <Query<ActuatorAccessoriesPageData, ActuatorAccessoriesPageVariables>
                query={pageConfig.query}
                variables={variables}
            >
                {({ data }) => {
                    if (data === undefined) {
                        return 'Loading...';
                    }
                    const accessories = prepareAccessories<
                    ActuatorAccessoriesPageData['accessories']['accessories']['0']
                    >(
                        data.accessories.accessories,
                        locationState.selectedAccessories,
                        userSettings,
                        null,
                        (a, b) => sortAccessories(a.accessory, b.accessory),
                    );
                    return (
                        <>
                            <ProjectSelectionDeviceList
                                title={<I18n messageId={MESSAGES.checkoutActuatorTitle}/>}
                            >
                                <ProjectSelectionDevice
                                    productImage={(
                                        <ProductImage
                                            imgSrc={data.accessories.actuator.image?.raw.join('') || ''}
                                        >
                                            <ActuatorIcon/>
                                        </ProductImage>
                                    )}
                                    productLink={(
                                        <ViewProductLink
                                            userSettings={userSettings}
                                            code={data.accessories.actuator.Label}
                                            content=""
                                        >
                                            <span className="vss-device__product-label">
                                                {data.accessories.actuator.Label}
                                            </span>

                                            <div className="vss-device__product-icon">
                                                <ArrowRightIcon/>
                                            </div>
                                        </ViewProductLink>
                                    )}
                                    productDescription={(
                                        <RenderHtml
                                            data={data.accessories.actuator.productType?.localized.join('') || ''}
                                        />
                                    )}
                                    quantity={locationState.qty}
                                />
                            </ProjectSelectionDeviceList>

                            {accessories.length ? (
                                <ProjectSelectionDeviceList
                                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                                >
                                    {accessories.map(({ qty, accessory: { item } }) => (
                                        <ProjectSelectionDevice
                                            key={item.Label}
                                            productImage={(
                                                <ProductImage
                                                    imgSrc={item.image?.raw.join('') || ''}
                                                >
                                                    <ActuatorIcon/>
                                                </ProductImage>
                                            )}
                                            productLink={(
                                                <ViewProductLink
                                                    userSettings={userSettings}
                                                    code={item.Label}
                                                    content=""
                                                >
                                                    {item.Label}

                                                    <div className="vss-accessory__product-icon">
                                                        <ArrowRightIcon/>
                                                    </div>
                                                </ViewProductLink>
                                            )}
                                            productDescription={item.accessoryType?.localized.join('') || ''}
                                            quantity={qty}
                                        />
                                    ))}
                                </ProjectSelectionDeviceList>
                            ) : null}
                        </>
                    );
                }}
            </Query>
        </Suspense>
    );
};
